import { api } from '../services/axiosConfig';

export type WebhookScopeType = 'global' | 'corp' | 'alliance' | 'vault';

/** The API never returns a webhook's real URL — only this masked stub. A
 * Discord webhook URL is a credential, so editing one means replacing it,
 * not loading it back into the form. */
export interface Webhook {
  id: number;
  label: string;
  urlMasked: string;
  events: string[];
  enabled: boolean;
  created_at: string;
  scope_type: WebhookScopeType;
  scope_id: number | null;
  scope_name: string | null;
  /** Discord's `content` field — a role/@here/@everyone mention sent
   * alongside the embed. Null means no ping. */
  mention: string | null;
  /** Whether this webhook can still deliver, derived server-side from the
   * last send: 'untested' (never fired), 'ok', 'failing' (transient), 'dead'
   * (Discord says the webhook no longer exists — auto-disabled). */
  health: 'untested' | 'ok' | 'failing' | 'dead';
  last_error: string | null;
  last_delivered_at: string | null;
  consecutive_failures: number;
}

export interface WebhookEventOption {
  key: string;
  label: string;
}

export const fetchWebhookEvents = () =>
  api.get<WebhookEventOption[]>('/api/admin/webhook-events').then((r) => r.data);

export const fetchWebhooks = () => api.get<Webhook[]>('/api/admin/webhooks').then((r) => r.data);

export interface WebhookScopeFields {
  scope_type: WebhookScopeType;
  scope_id: number | null;
  scope_name: string | null;
}

export const createWebhook = (
  body: { label: string; url: string; events: string[]; enabled?: boolean; mention?: string | null } & Partial<WebhookScopeFields>,
) => api.post<Webhook>('/api/admin/webhooks', body).then((r) => r.data);

export const updateWebhook = (
  id: number,
  body: Partial<{ label: string; url: string; events: string[]; enabled: boolean; mention: string | null } & WebhookScopeFields>,
) => api.put<Webhook>(`/api/admin/webhooks/${id}`, body).then((r) => r.data);

export const deleteWebhook = (id: number) => api.delete(`/api/admin/webhooks/${id}`);

export const testWebhook = (id: number) => api.post<{ ok: true }>(`/api/admin/webhooks/${id}/test`).then((r) => r.data);
